import { createSelector } from '@reduxjs/toolkit';
import { keyBy, orderBy } from 'es-toolkit';

import { CONSTANTS } from '@/config/constants';
import { DEFAULT_GEOMETRY_CLASS_DEFINITION } from '@/config/defaults';
import {
  CustomGeometryClassDefinition,
  CustomGeometryInstance,
} from '@/features/custom-shared/types/editor/custom';
import type { RootState } from '@/stores/store';
import { CustomGeometryClassNames } from '@/types/custom';

import {
  GeometryProjectFileWrapper,
  JSONProjectFile,
} from '../../../custom-shared/types/project';
import { getNNPrefix, getNNVariantName } from '../../utils/custom';
import { createInstanceWrapper } from '../../utils/project-files';

export const selectProjectFeature = (s: RootState) => s.editor.project;

export const selectProjectFilesMap = createSelector(
  [selectProjectFeature],
  (s) => s.files,
);

const selectDefinitionFiles = createSelector(
  [selectProjectFeature],
  (s) => s.definitionFiles,
);

const selectProjectFiles = createSelector(
  [selectProjectFeature],
  (s) => s.files,
);

export const selectProject = createSelector(
  [selectProjectFeature],
  (s) => s.project,
);

export const selectProjectError = createSelector(
  [selectProjectFeature],
  (s) => s.error,
);

export const selectIsLocalProject = createSelector(
  [selectProject],
  (project) =>
    (project &&
      project.id.startsWith(CONSTANTS.projects.localProjectsPrefix)) ||
    false,
);

const _selectBaseNameParam = (_: RootState, baseName: string) => baseName;
const _selectGeometryClassParam = <
  T extends CustomGeometryClassNames = CustomGeometryClassNames,
>(
  _: RootState,
  className: T,
) => className;
const _selectGeometryClassParamWBaseName = <
  T extends CustomGeometryClassNames = CustomGeometryClassNames,
>(
  _: RootState,
  _1: string,
  className: T,
) => className;

export const selectProjectFileByName = createSelector(
  [_selectBaseNameParam, selectProjectFilesMap],
  (baseName, files) => files[baseName] || null,
);

export const selectProjectDefinitionFileByName = createSelector(
  [_selectBaseNameParam, selectDefinitionFiles],
  (baseName, files) => files[baseName] || null,
);

export const selectProjectJSONFile = createSelector(
  [selectProjectFileByName],
  (files) => (files && files.json) || null,
);

const selectProjectJSON = createSelector(
  [selectProjectJSONFile],
  (file) => (file && file.content) || null,
);

export const selectProjectSTLFile = createSelector(
  [selectProjectFileByName],
  (files) => (files && files.stl) || null,
);

export const selectDefinitionJSONFileTyped = <
  T extends CustomGeometryClassNames = CustomGeometryClassNames,
>() =>
  createSelector(
    [_selectGeometryClassParam<T>, selectDefinitionFiles],
    (geometryClass, files) => files[geometryClass] || null,
  );

export const selectDefinitionJSONTyped = <
  T extends CustomGeometryClassDefinition,
>() =>
  createSelector(
    [selectDefinitionJSONFileTyped<T['name']>()],
    (file) => (file && (file.content as T)) || null,
  );

export const selectDefinitionJSONUntyped =
  selectDefinitionJSONTyped<CustomGeometryClassDefinition>();

const selectJSONGeometryClassName = createSelector(
  [selectProjectJSON],
  (json) =>
    (json && 'className' in json && json.className) as CustomGeometryClassNames,
);

export const selectSTLGeometryClassDefinition = createSelector(
  [selectJSONGeometryClassName, selectDefinitionFiles],
  (className, definitionFiles) =>
    definitionFiles[className]?.content ||
    // class definition may be missing
    DEFAULT_GEOMETRY_CLASS_DEFINITION,
);

export const selectSTLGeometryInstanceTyped = <
  T extends CustomGeometryInstance,
>() =>
  createSelector(
    [_selectBaseNameParam, selectSTLGeometryClassDefinition, selectProjectJSON],
    (baseName, definition, json) =>
      createInstanceWrapper<T>({ baseName, definition, json: json as any }),
  );

export const selectSTLGeometryInstanceUntyped =
  selectSTLGeometryInstanceTyped<CustomGeometryInstance>();

export const selectAllSTLFiles = createSelector([selectProjectFeature], (s) =>
  Object.values(s.files)
    .map((f) => (f && 'stl' in f ? f.stl : null))
    .filter((f) => !!f),
);

/**
 * This selector returns all the files that should be considered for rendering inside the
 * viewer, also files that has only JSON file and not an STL file: this to enable
 * plugin working without STL file still
 * @param geometryClass
 * @returns
 */
export const selectProjectFilesByGeometryClass = createSelector(
  [_selectGeometryClassParam, selectProjectFiles],
  (geometryClass, files) =>
    Object.values(files).filter(
      (f) =>
        (f &&
          f.json &&
          (f.json.content as CustomGeometryInstance).className ===
            geometryClass) ||
        geometryClass === DEFAULT_GEOMETRY_CLASS_DEFINITION.name ||
        false,
    ) as GeometryProjectFileWrapper[],
);

export const selectAllInstanceFilesByGeometryClassTyped = <
  T extends CustomGeometryInstance,
>() =>
  createSelector(
    [_selectGeometryClassParam, selectProjectFilesByGeometryClass],
    (geometryClass, files) =>
      orderBy(
        Object.values(files).filter(
          (file) =>
            (file.json.content as CustomGeometryInstance).className ===
            geometryClass,
        ),
        [(i) => i.json.content.displayProperties.name, (i) => i.json.baseName],
        ['asc', 'asc'],
      ).map((i) => i.json) as JSONProjectFile<T>[],
  );
export const selectAllInstanceFilesByGeometryClassUnTyped =
  selectAllInstanceFilesByGeometryClassTyped<CustomGeometryInstance>();

export const selectAllGeometryClasses = createSelector(
  [selectProjectFeature],
  (s) => Object.keys(s.definitionFiles) as CustomGeometryClassNames[],
);

export const selectProjectHasSTLs = createSelector(
  [selectAllSTLFiles],
  (s) => s.length,
);

export const selectAllDefinitions = createSelector(
  [selectDefinitionFiles],
  (definitionFiles) => {
    const asArray = Object.values(definitionFiles)
      .filter((df) => !!df)
      .map((df) => df.content);

    return orderBy(asArray, [(df) => df.displayProperties.order], ['asc']);
  },
);

export const selectAllInstanceFiles = createSelector(
  [selectProjectFeature],
  (s) =>
    Object.values(s.files)
      .map((f) => (f && 'json' in f ? f.json : null))
      .filter(Boolean) as JSONProjectFile<CustomGeometryInstance>[],
);

/**
 * Returns all the geometry instances where the baseName is used as a value
 * of a property with $references = geometryClass
 * @param geometryClass properties should have this geometryClass as $references
 * @param baseName instance name to search for
 * @returns a list of ProjectFile<CustomGeometryInstance>[] referring the passed geometry instance
 */
export const selectAllInstanceWhereReferenced = createSelector(
  [
    _selectBaseNameParam,
    _selectGeometryClassParamWBaseName,
    selectAllInstanceFiles,
    selectDefinitionFiles,
  ],
  (baseName, geometryClass, instances, definitions) =>
    instances.filter((i) => {
      const props = i.content.properties;

      for (const propKey of Object.keys(props)) {
        const prop = props[propKey];
        const definition = definitions[i.content.className];

        // check if property value is == to searched baseName
        // and related definition has a $references with the search geometryClass
        if (
          definition &&
          prop === baseName &&
          definition.content.propertiesSchema[propKey] &&
          '$references' in definition.content.propertiesSchema[propKey] &&
          definition.content.propertiesSchema[propKey].$references &&
          definition.content.propertiesSchema[propKey].$references ===
            geometryClass
        ) {
          return true;
        }
      }

      return false;
    }),
);

export const selectAllNNResultVariants = createSelector(
  [_selectBaseNameParam, selectAllInstanceFiles],
  (baseName, files) =>
    keyBy(
      files.filter(
        (f) =>
          f.content.className === 'NNResult' &&
          f.baseName.startsWith(getNNPrefix(baseName)),
      ),
      (item) => getNNVariantName(item.baseName),
    ),
);
